import { CardDecorativeGradient } from '@mdvip/shared/components/blocks/CardDecorativeGradient';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { CardContent } from '@mdvip/shared/components/ui/card';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { Trans, useTranslation } from 'react-i18next';

export interface ContactInfoProps {
  /** Called when the member clicks "Contact us". A future change wires this to the real Contact Us modal. */
  onOpenContact: () => void;
}

/** Matches Navigation.tsx's own `tel:${phone.replace(/\./g, '')}` transform. */
function supportTel(phone: string) {
  return `tel:${phone.replace(/\./g, '')}`;
}

/**
 * ContactInfo: the "Get in Touch with MDVIP" section — three cards (Contact / Call Us / MDVIP
 * Headquarters). Built from the design's Concept A · Classic Banner, minus the HQ photo (removed
 * per product decision — see 00-story.md's Resolved Conflicts).
 *
 * Presentational: `onOpenContact` is supplied by whatever mounts this, which is also what owns the
 * Contact Us modal's open state. It lives in `features/contact/` rather than `components/blocks/`
 * because it is a feature slice, not a generic cross-feature primitive.
 */
export function ContactInfo({ onOpenContact }: ContactInfoProps) {
  const { t } = useTranslation('shared');

  return (
    <section className="@container flex flex-col gap-6">
      <div className="border-b-2 border-border pb-4">
        <h2 className="text-h3 font-semibold text-foreground">{t('contact.sectionTitle')}</h2>
      </div>
      <div className="grid grid-cols-1 gap-6 @[640px]:grid-cols-2 @[1024px]:grid-cols-3">
        <CardDecorativeGradient
          tone="primary"
          className="h-full"
          decoration={
            <Icon
              name="messageSquare"
              weight="solid"
              className="absolute -right-4 top-4 text-[116px] text-primary/15"
            />
          }
        >
          <CardContent className="flex flex-1 flex-col gap-3 pt-6">
            <h3 className="text-lg font-semibold text-foreground">{t('contact.contactCard.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('contact.contactCard.description')}</p>
            <Button className="mt-auto self-start" onClick={onOpenContact}>
              {t('contact.contactCard.button')}
            </Button>
          </CardContent>
        </CardDecorativeGradient>

        <CardDecorativeGradient
          tone="primary"
          className="h-full"
          decoration={
            <Icon name="phone" weight="solid" className="absolute -right-4 top-4 text-[116px] text-primary/15" />
          }
        >
          <CardContent className="flex flex-col gap-3 pt-6">
            <h3 className="text-lg font-semibold text-foreground">{t('contact.callCard.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('contact.callCard.hours')}</p>
            <p className="text-sm text-muted-foreground">
              <Trans
                t={t}
                i18nKey="contact.callCard.description"
                values={{ phone: MDVIP_COMPANY_INFO.memberSupportPhone }}
                components={{
                  // The translation string's own <phoneLink> content ({phone}, interpolated)
                  // becomes this link's children — a translator can freely reposition the
                  // phone number and trailing punctuation relative to the surrounding sentence.
                  phoneLink: (
                    // biome-ignore lint/a11y/useAnchorContent: Trans fills this <a>'s children at runtime from the translation string's own <phoneLink> content — the static analyzer can't see that.
                    <a
                      href={supportTel(MDVIP_COMPANY_INFO.memberSupportPhone)}
                      className="font-medium text-foreground underline decoration-from-font underline-offset-2 hover:text-muted-foreground"
                    />
                  ),
                }}
              />
            </p>
          </CardContent>
        </CardDecorativeGradient>

        <CardDecorativeGradient
          tone="primary"
          className="h-full"
          decoration={
            <Icon name="mapPin" weight="solid" className="absolute -right-4 top-4 text-[116px] text-primary/15" />
          }
        >
          <CardContent className="flex flex-col gap-3 pt-6">
            <h3 className="text-lg font-semibold text-foreground">{t('contact.headquartersCard.title')}</h3>
            {/* No HQ photo — excluded per product decision (00-story.md Resolved Conflicts). */}
            <div className="text-sm text-muted-foreground">
              {MDVIP_COMPANY_INFO.hqAddress.line1}
              <br />
              {MDVIP_COMPANY_INFO.hqAddress.line2}
              <a
                href={`https://${MDVIP_COMPANY_INFO.website}`}
                target="_blank"
                rel="noreferrer"
                className="mt-1 flex items-center gap-1 font-medium text-foreground hover:text-muted-foreground"
              >
                <Icon name="globe" size="xs" />
                <span className="underline decoration-from-font underline-offset-2">{MDVIP_COMPANY_INFO.website}</span>
                <Icon name="externalLink" size="xs" />
              </a>
            </div>
          </CardContent>
        </CardDecorativeGradient>
      </div>
    </section>
  );
}
